import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Trash2,
  Info,
} from "lucide-react";

const NonOverlappingIntervalsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Basic example",
      intervals: [
        [1, 2],
        [2, 3],
        [3, 4],
        [1, 3],
      ],
      expected: 1,
    },
    {
      id: 2,
      name: "Multiple overlaps",
      intervals: [
        [1, 2],
        [1, 2],
        [1, 2],
      ],
      expected: 2,
    },
    {
      id: 3,
      name: "No removal needed",
      intervals: [
        [1, 2],
        [2, 3],
      ],
      expected: 0,
    },
    {
      id: 4,
      name: "Chain of overlaps",
      intervals: [
        [1, 100],
        [11, 22],
        [1, 11],
        [2, 12],
      ],
      expected: 2,
    },
    {
      id: 5,
      name: "Complex case",
      intervals: [
        [0, 2],
        [1, 3],
        [2, 4],
        [3, 5],
        [4, 6],
      ],
      expected: 2,
    },
    {
      id: 6,
      name: "Nested intervals",
      intervals: [
        [1, 5],
        [2, 3],
        [3, 4],
      ],
      expected: 2,
    },
    {
      id: 7,
      name: "Single interval",
      intervals: [[1, 2]],
      expected: 0,
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customIntervals, setCustomIntervals] = useState("");
  const [intervals, setIntervals] = useState(testCases[0].intervals);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function eraseOverlapIntervals(intervals):", indent: 0 },
    { line: "// Sort by end time (greedy choice)", indent: 1 },
    { line: "intervals.sort((a, b) => a[1] - b[1])", indent: 1 },
    { line: "", indent: 0 },
    { line: "count = 0  // intervals to remove", indent: 1 },
    { line: "prevEnd = intervals[0][1]", indent: 1 },
    { line: "", indent: 0 },
    { line: "for i = 1 to intervals.length:", indent: 1 },
    { line: "// Check if current overlaps with previous", indent: 2 },
    { line: "if intervals[i][0] < prevEnd:", indent: 2 },
    { line: "// Overlap found, remove current", indent: 3 },
    { line: "count++", indent: 3 },
    { line: "else:", indent: 2 },
    { line: "// No overlap, update prevEnd", indent: 3 },
    { line: "prevEnd = intervals[i][1]", indent: 3 },
    { line: "", indent: 0 },
    { line: "return count", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (inputIntervals) => {
    if (inputIntervals.length <= 1) {
      return [
        {
          step: 0,
          intervals: inputIntervals,
          kept: inputIntervals.map((_, idx) => idx),
          removed: [],
          phase: "complete",
          description: "No intervals to remove",
          highlightLine: 0,
          removedCount: 0,
          isFinal: true,
        },
      ];
    }

    const allSteps = [];
    let stepCount = 0;

    // Step 1: Show original intervals
    allSteps.push({
      step: stepCount++,
      intervals: [...inputIntervals],
      kept: [],
      removed: [],
      phase: "init",
      description: "Original intervals",
      highlightLine: 0,
      removedCount: 0,
    });

    // Step 2: Sort by end time
    const sortedWithIndex = inputIntervals
      .map((interval, idx) => ({ interval, originalIndex: idx }))
      .sort((a, b) => a.interval[1] - b.interval[1]);

    const sorted = sortedWithIndex.map((item) => item.interval);
    const originalIndices = sortedWithIndex.map((item) => item.originalIndex);

    allSteps.push({
      step: stepCount++,
      intervals: sorted,
      originalIndices,
      kept: [],
      removed: [],
      phase: "sort",
      description: "Sort intervals by end time (greedy strategy)",
      highlightLine: 2,
      removedCount: 0,
    });

    // Step 3: Greedy selection
    const kept = [0]; // Keep first interval
    const removed = [];
    let prevEnd = sorted[0][1];
    let count = 0;

    allSteps.push({
      step: stepCount++,
      intervals: sorted,
      kept: [...kept],
      removed: [...removed],
      currentIndex: 0,
      prevEnd,
      phase: "init-greedy",
      description: `Keep first interval [${sorted[0][0]},${sorted[0][1]}], set prevEnd = ${prevEnd}`,
      highlightLine: 5,
      removedCount: 0,
    });

    // Process each interval
    for (let i = 1; i < sorted.length; i++) {
      const currentInterval = sorted[i];
      const overlaps = currentInterval[0] < prevEnd;

      // Show comparison
      allSteps.push({
        step: stepCount++,
        intervals: sorted,
        kept: [...kept],
        removed: [...removed],
        currentIndex: i,
        prevEnd,
        phase: "compare",
        description: `Check interval [${currentInterval[0]},${currentInterval[1]}]: start (${currentInterval[0]}) < prevEnd (${prevEnd})?`,
        highlightLine: 9,
        removedCount: count,
        comparison: {
          current: currentInterval,
          prevEnd,
          overlaps,
          condition: `${currentInterval[0]} < ${prevEnd}`,
        },
      });

      if (overlaps) {
        // Remove current interval
        removed.push(i);
        count++;

        allSteps.push({
          step: stepCount++,
          intervals: sorted,
          kept: [...kept],
          removed: [...removed],
          currentIndex: i,
          prevEnd,
          phase: "remove",
          description: `Overlap detected! Remove interval [${currentInterval[0]},${currentInterval[1]}]`,
          highlightLine: 11,
          removedCount: count,
          justRemoved: i,
        });
      } else {
        // Keep current interval
        kept.push(i);
        prevEnd = currentInterval[1];

        allSteps.push({
          step: stepCount++,
          intervals: sorted,
          kept: [...kept],
          removed: [...removed],
          currentIndex: i,
          prevEnd,
          phase: "keep",
          description: `No overlap. Keep interval [${currentInterval[0]},${currentInterval[1]}], update prevEnd = ${prevEnd}`,
          highlightLine: 14,
          removedCount: count,
          justKept: i,
        });
      }
    }

    // Final state
    allSteps.push({
      step: stepCount++,
      intervals: sorted,
      kept: [...kept],
      removed: [...removed],
      phase: "complete",
      description: `Complete! Removed ${count} interval${
        count !== 1 ? "s" : ""
      } to make all non-overlapping`,
      highlightLine: 16,
      removedCount: count,
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps = calculateSteps(intervals);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [intervals]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setIntervals(testCases[index].intervals);
  };

  const parseIntervals = (str) => {
    if (!str.trim()) return [];

    const intervalMatches = str.match(/\[?\s*-?\d+\s*,\s*-?\d+\s*\]?/g);
    if (!intervalMatches) return null;

    const parsed = [];
    for (const match of intervalMatches) {
      const nums = match.match(/-?\d+/g);
      if (nums && nums.length === 2) {
        const start = parseInt(nums[0]);
        const end = parseInt(nums[1]);
        if (start > end) return null;
        parsed.push([start, end]);
      }
    }

    return parsed.length > 0 ? parsed : null;
  };

  const calculateExpected = (intervals) => {
    if (intervals.length <= 1) return 0;

    const sorted = [...intervals].sort((a, b) => a[1] - b[1]);
    let count = 0;
    let prevEnd = sorted[0][1];

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i][0] < prevEnd) {
        count++;
      } else {
        prevEnd = sorted[i][1];
      }
    }

    return count;
  };

  const addCustomTestCase = () => {
    try {
      const parsedIntervals = parseIntervals(customIntervals);

      if (!parsedIntervals) {
        alert("Invalid format. Use: [[1,2],[2,3],[3,4]]");
        return;
      }

      const expected = calculateExpected(parsedIntervals);

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        intervals: parsedIntervals,
        expected: expected,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomIntervals("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  // Generate timeline visualization
  const generateTimeline = () => {
    if (!currentStepData?.intervals || currentStepData.intervals.length === 0)
      return null;

    const allValues = currentStepData.intervals.flat();
    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);
    const range = maxVal - minVal;
    const scale = 100 / (range + 2);

    return { minVal, maxVal, scale };
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Trash2 className="text-blue-500" size={24} />
        Non-overlapping Intervals
      </h2>

      {/* Problem Statement */}
      <div
        className={`mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200 ${
          showProblem ? "" : "hidden"
        }`}
      >
        <div className="flex items-start gap-2">
          <Info className="text-blue-600 mt-1" size={16} />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">
              Problem Statement
            </h3>
            <p className="text-sm text-blue-800 mb-2">
              Given an array of intervals where intervals[i] = [start_i, end_i],
              return the minimum number of intervals you need to remove to make
              the rest of the intervals non-overlapping.
            </p>
            <p className="text-sm text-blue-800 mb-2">
              Note: Intervals which only touch at a point are non-overlapping.
              For example, [1,2] and [2,3] are non-overlapping.
            </p>
            <div className="mt-2 p-2 bg-white rounded">
              <p className="text-xs font-medium text-gray-700 mb-1">Example:</p>
              <p className="text-xs font-mono text-gray-600">
                Input: intervals = [[1,2],[2,3],[3,4],[1,3]]
                <br />
                Output: 1<br />
                Explanation: [1,3] can be removed and the rest are
                non-overlapping.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowProblem(false)}
            className="text-blue-600 hover:text-blue-800"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {!showProblem && (
        <button
          onClick={() => setShowProblem(true)}
          className="mb-4 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <Info size={14} />
          Show problem statement
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      Input:{" "}
                      {tc.intervals
                        .map((int) => `[${int[0]},${int[1]}]`)
                        .join(", ")}
                    </div>
                    <div className="text-xs opacity-75">
                      Minimum removals: {tc.expected}
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customIntervals}
                onChange={(e) => setCustomIntervals(e.target.value)}
                placeholder="Enter intervals: [[1,2],[2,3],[3,4],[1,3]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Interval Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Comparison Display */}
            {currentStepData?.comparison && (
              <div className="mb-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs text-yellow-800 font-medium">
                  Checking: {currentStepData.comparison.condition} ={" "}
                  {currentStepData.comparison.overlaps
                    ? "true (overlap - remove)"
                    : "false (no overlap - keep)"}
                </p>
              </div>
            )}

            {/* Timeline Visualization */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Timeline View:
              </p>
              <div className="relative h-64 bg-gray-50 rounded p-2">
                {generateTimeline() && (
                  <>
                    {/* Grid lines */}
                    {[
                      ...Array(
                        Math.ceil(
                          generateTimeline().maxVal -
                            generateTimeline().minVal +
                            3
                        )
                      ),
                    ].map((_, i) => {
                      const val = generateTimeline().minVal + i;
                      return (
                        <div
                          key={i}
                          className="absolute top-0 bottom-0 border-l border-gray-300"
                          style={{ left: `${i * generateTimeline().scale}%` }}
                        >
                          <span className="absolute -bottom-5 -left-2 text-xs text-gray-500">
                            {val}
                          </span>
                        </div>
                      );
                    })}

                    {/* Intervals */}
                    {currentStepData.intervals.map((interval, idx) => {
                      const isKept = currentStepData.kept.includes(idx);
                      const isRemoved = currentStepData.removed.includes(idx);
                      const isCurrent = idx === currentStepData.currentIndex;
                      const justRemoved = idx === currentStepData.justRemoved;
                      const justKept = idx === currentStepData.justKept;

                      return (
                        <div
                          key={`interval-${idx}`}
                          className={`absolute h-8 rounded transition-all duration-300 ${
                            justRemoved
                              ? "bg-red-400 border-2 border-red-600 animate-pulse"
                              : justKept
                              ? "bg-green-400 border-2 border-green-600 animate-pulse"
                              : isRemoved
                              ? "bg-red-200 border border-red-400 opacity-50 line-through"
                              : isKept
                              ? "bg-green-200 border border-green-400"
                              : isCurrent
                              ? "bg-yellow-300 border-2 border-yellow-500"
                              : "bg-blue-200 border border-blue-300"
                          }`}
                          style={{
                            left: `${
                              (interval[0] - generateTimeline().minVal) *
                              generateTimeline().scale
                            }%`,
                            width: `${
                              (interval[1] - interval[0]) *
                              generateTimeline().scale
                            }%`,
                            top: `${20 + idx * 15}px`,
                          }}
                        >
                          <span
                            className={`text-xs px-1 ${
                              isRemoved ? "line-through" : ""
                            }`}
                          >
                            [{interval[0]},{interval[1]}]
                          </span>
                        </div>
                      );
                    })}

                    {/* Previous end line */}
                    {currentStepData.prevEnd !== undefined && (
                      <div
                        className="absolute top-0 bottom-8 border-l-2 border-orange-500"
                        style={{
                          left: `${
                            (currentStepData.prevEnd -
                              generateTimeline().minVal) *
                            generateTimeline().scale
                          }%`,
                        }}
                      >
                        <span className="absolute -top-5 -left-8 text-xs text-orange-600 font-medium">
                          prevEnd: {currentStepData.prevEnd}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-50 p-2 rounded">
                <div className="text-xs text-green-600">Kept</div>
                <div className="font-semibold text-green-800">
                  {currentStepData?.kept?.length || 0}
                </div>
              </div>
              <div className="bg-red-50 p-2 rounded">
                <div className="text-xs text-red-600">Removed</div>
                <div className="font-semibold text-red-800">
                  {currentStepData?.removedCount || 0}
                </div>
              </div>
              <div className="bg-blue-50 p-2 rounded">
                <div className="text-xs text-blue-600">Total</div>
                <div className="font-semibold text-blue-800">
                  {currentStepData?.intervals?.length || 0}
                </div>
              </div>
            </div>

            {/* Final Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  Solution Found!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Minimum removals: {currentStepData.removedCount} interval
                  {currentStepData.removedCount !== 1 ? "s" : ""}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Remaining intervals are all non-overlapping
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5 overflow-y-auto max-h-96">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="space-y-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Time Complexity: O(n log n)
                </h4>
                <p className="text-xs text-gray-600">
                  Dominated by sorting. The greedy selection is O(n).
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Space Complexity: O(1)
                </h4>
                <p className="text-xs text-gray-600">
                  Only using constant extra space (excluding sort)
                </p>
              </div>
            </div>
          </div>

          {/* Algorithm Strategy */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Greedy Strategy</h3>
            <div className="text-xs text-gray-700 space-y-2">
              <p className="font-medium">
                Key Insight: Sort by end time and greedily select intervals
              </p>
              <ol className="space-y-1 ml-4">
                <li>1. Sort intervals by their end time</li>
                <li>2. Always keep the interval that ends earliest</li>
                <li>
                  3. Remove any interval that overlaps with kept intervals
                </li>
              </ol>
              <p className="mt-2 text-gray-600">
                This greedy choice is optimal because keeping intervals that end
                early leaves more room for future intervals.
              </p>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Sorting by end time (not start time) is crucial for the greedy
                  approach
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Intervals touching at a point [1,2] and [2,3] don't overlap
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The problem is equivalent to finding maximum non-overlapping
                  intervals
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Greedy choice: always keep the interval that ends earliest
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>This is related to the Activity Selection problem</span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-blue-200 border border-blue-300 rounded"></div>
                <span>Unprocessed intervals</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-yellow-300 border-2 border-yellow-500 rounded"></div>
                <span>Currently checking</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-green-200 border border-green-400 rounded"></div>
                <span>Kept (non-overlapping)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-red-200 border border-red-400 rounded opacity-50">
                  <div className="line-through text-center">interval</div>
                </div>
                <span>Removed (overlapping)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="border-l-2 border-orange-500 h-6"></div>
                <span>Previous end boundary</span>
              </div>
            </div>
          </div>

          {/* Example Walkthrough */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Why Sort by End Time?
            </h3>
            <div className="text-xs text-gray-700 space-y-2">
              <p className="font-medium">Consider: [[1,100], [2,3], [4,5]]</p>
              <div className="space-y-1">
                <p>✅ Sort by end: [[2,3], [4,5], [1,100]]</p>
                <p className="ml-3 text-gray-600">
                  → Keep [2,3] and [4,5], remove 1
                </p>
              </div>
              <div className="space-y-1 mt-2">
                <p>❌ Sort by start: [[1,100], [2,3], [4,5]]</p>
                <p className="ml-3 text-gray-600">
                  → Keep [1,100], remove 2 (not optimal)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NonOverlappingIntervalsVisualizer;
